import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Avatar,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Grid,
    Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as S from "./styles";
import { apiGetRankingCreator } from "../../services/apiGetRankingCreator";
import { formatDateString } from "../../utils/formatDateString";

const POINT_TYPE_LABELS = {
    LIKE: "Curtida",
    POST: "Publicação",
    COMMENT: "Comentário",
    DESAFIO: "Desafio",
    DOWNLOAD: "Download",
    LINK: "Clique em link",
};

const POINT_TYPE_ACTIONS = {
    LIKE: "Curtiu um vídeo",
    POST: "Publicou um vídeo",
    COMMENT: "Fez um comentário",
    DESAFIO: "Completou um desafio",
    DOWNLOAD: "Baixou um vídeo",
    LINK: "Clicou em um link",
};

const INTERACTION_LIMIT_PER_PERIOD = 50;

const INTERACTION_LIMIT_MESSAGES = {
    LIKE: `Limite de pontuação para likes do período atingido (${INTERACTION_LIMIT_PER_PERIOD} likes)`,
    COMMENT: `Limite de pontuação para comentários do período atingido (${INTERACTION_LIMIT_PER_PERIOD} comentários)`,
};

const isStarType = (tipo) => tipo?.startsWith("ESTRELA_");

const getStarCount = (tipo) => tipo.replace("ESTRELA_", "");

const getPointTypeLabel = (tipo) => {
    if (isStarType(tipo)) return `${getStarCount(tipo)} estrelas`;
    return POINT_TYPE_LABELS[tipo] || tipo;
};

const PointDescription = ({ tipo, points, overInteractionLimit }) => {
    if (isStarType(tipo)) {
        return (
            <>
                O vídeo recebeu <b>{getStarCount(tipo)} estrelas</b> e rendeu <b>{points} pontos</b>
            </>
        );
    }

    const action = POINT_TYPE_ACTIONS[tipo];
    if (action && overInteractionLimit) {
        return <>{action}</>;
    }

    if (action) {
        return (
            <>
                {action} e ganhou <b>{points} pontos</b>
            </>
        );
    }

    return (
        <>
            Ganhou <b>{points} pontos</b> por atividade do tipo <b>{tipo}</b>
        </>
    );
};

export const RankingDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const creatorId = queryParams.get("creator");
    const comunidadeId = queryParams.get("comunidade");
    const periodoId = queryParams.get("periodo");

    const [creatorData, setCreatorData] = useState(null);
    const [pointDetails, setPointDetails] = useState([]);
    const [pointsByType, setPointsByType] = useState({});
    const [desafiosCount, setDesafiosCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCreatorRanking = async () => {
            try {
                setIsLoading(true);
                const response = await apiGetRankingCreator({
                    creator: creatorId,
                    comunidade: comunidadeId,
                    periodo: periodoId,
                });

                if (response && Array.isArray(response)) {
                    if (response.length > 0) {
                        const firstItem = response[0];
                        const creatorInfo = {
                            name: firstItem.creator_name,
                            avatar_url: "", // A API não está retornando avatar
                            position: "", // A API não está retornando posição
                            total_points: response.reduce((sum, item) => sum + item.points, 0),
                        };
                        setCreatorData(creatorInfo);
                        setPointDetails(response);

                        // Calcular soma de pontos por tipo
                        const pointsGroupedByType = {};
                        response.forEach((point) => {
                            if (!pointsGroupedByType[point.tipo]) {
                                pointsGroupedByType[point.tipo] = 0;
                            }
                            pointsGroupedByType[point.tipo] += point.points;
                        });
                        setPointsByType(pointsGroupedByType);

                        // Contar desafios
                        const desafios = response.filter((point) => point.tipo === "DESAFIO");
                        setDesafiosCount(desafios.length);
                    }
                } else if (response) {
                    // Caso a API retorne o formato esperado anteriormente
                    setCreatorData(response.creator);
                    setPointDetails(response.points || []);

                    // Calcular soma de pontos por tipo
                    const pointsGroupedByType = {};
                    (response.points || []).forEach((point) => {
                        if (!pointsGroupedByType[point.tipo]) {
                            pointsGroupedByType[point.tipo] = 0;
                        }
                        pointsGroupedByType[point.tipo] += point.points;
                    });
                    setPointsByType(pointsGroupedByType);
                }
            } catch (err) {
                setError("Não foi possível carregar os detalhes do ranking");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        if (creatorId && comunidadeId && periodoId) {
            fetchCreatorRanking();
        } else {
            setError("Parâmetros necessários não encontrados");
            setIsLoading(false);
        }
    }, [creatorId, comunidadeId, periodoId]);

    const handleGoBack = () => {
        navigate(-1);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <S.Container>
            <Box sx={{ px: 2, pt: 3, pb: { xs: 10, sm: 12 } }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <S.Title>Detalhes do Ranking</S.Title>
                </Box>

                {creatorData && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 2,
                            mb: 3,
                            borderRadius: 2,
                            bgcolor: "rgba(0, 163, 255, 0.1)",
                        }}
                    >
                        <Avatar src={creatorData.avatar_url} sx={{ width: 64, height: 64, mr: 2 }} />
                        <Box>
                            <Typography variant="h6">{creatorData.name}</Typography>

                            <Typography variant="body1" fontWeight="bold" color="#00A3FF">
                                Total de pontos: {creatorData.total_points}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {Object.keys(pointsByType).length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            RESUMO POR TIPO DE PONTOS
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", mx: -1 }}>
                            {Object.entries(pointsByType).map(([tipo, totalPontos], index) => {
                                const cardColors = [
                                    { bg: "rgba(0, 163, 255, 0.1)", border: "rgba(0, 163, 255, 0.3)", text: "#0088d6" },
                                    { bg: "rgba(76, 175, 80, 0.1)", border: "rgba(76, 175, 80, 0.3)", text: "#388e3c" },
                                    { bg: "rgba(255, 152, 0, 0.1)", border: "rgba(255, 152, 0, 0.3)", text: "#f57c00" },
                                    {
                                        bg: "rgba(156, 39, 176, 0.1)",
                                        border: "rgba(156, 39, 176, 0.3)",
                                        text: "#7b1fa2",
                                    },
                                    { bg: "rgba(233, 30, 99, 0.1)", border: "rgba(233, 30, 99, 0.3)", text: "#c2185b" },
                                    { bg: "rgba(121, 85, 72, 0.1)", border: "rgba(121, 85, 72, 0.3)", text: "#5d4037" },
                                    { bg: "rgba(63, 81, 181, 0.1)", border: "rgba(19, 20, 23, 0.3)", text: "#303f9f" },
                                    { bg: "rgba(0, 150, 136, 0.1)", border: "rgba(0, 150, 136, 0.3)", text: "#00796b" },
                                    {
                                        bg: "rgba(103, 58, 183, 0.1)",
                                        border: "rgba(103, 58, 183, 0.3)",
                                        text: "#512da8",
                                    },
                                ];

                                const colorIndex = index % cardColors.length;
                                const currentColor = cardColors[colorIndex];

                                return (
                                    <Box
                                        key={tipo}
                                        sx={{
                                            width: { xs: "50%", sm: "33.33%", md: "25%", lg: "25%" },
                                            p: 1,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: 2,
                                                bgcolor: currentColor.bg,
                                                border: `1px solid ${currentColor.border}`,
                                                minHeight: "85px",
                                                height: "100%",
                                                width: "100%",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                align="center"
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    mb: 0.5,
                                                    lineHeight: 1.2,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: "100%",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {getPointTypeLabel(tipo)}
                                            </Typography>
                                            <Typography variant="h6" color={currentColor.text} fontWeight="bold">
                                                {totalPontos}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                );
                            })}
                            {desafiosCount > 0 && (
                                <Box
                                    sx={{
                                        width: { xs: "50%", sm: "33.33%", md: "25%", lg: "25%" },
                                        p: 1,
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: 2,
                                            bgcolor: "rgba(255, 87, 34, 0.1)",
                                            border: "1px solid rgba(255, 87, 34, 0.3)",
                                            minHeight: "85px",
                                            height: "100%",
                                            width: "100%",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            align="center"
                                            sx={{
                                                fontSize: "0.75rem",
                                                mb: 0.5,
                                                lineHeight: 1.2,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                width: "100%",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            No. DESAFIOS
                                        </Typography>
                                        <Typography variant="h6" color="#d84315" fontWeight="bold">
                                            {desafiosCount}
                                        </Typography>
                                    </Paper>
                                </Box>
                                )}
                        </Box>
                    </Box>
                )}

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        DETALHAMENTO DE PONTOS
                    </Typography>

                    {pointDetails.length > 0 ? (
                        <List
                            sx={{
                                bgcolor: "background.paper",
                                borderRadius: 2,
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            {pointDetails.map((point, index) => (
                                <React.Fragment key={index}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemText
                                            primary={
                                                <Typography fontWeight="bold">{getPointTypeLabel(point.tipo)}</Typography>
                                            }
                                            secondary={
                                                <>
                                                    <Typography component="span" variant="body2" color="text.primary">
                                                        <PointDescription
                                                            tipo={point.tipo}
                                                            points={point.points}
                                                            overInteractionLimit={point.over_interaction_limit}
                                                        />
                                                        {point.post ? (
                                                            <>
                                                                {" "}
                                                                no <b>Post #{point.post}</b>
                                                            </>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </Typography>
                                                    {point.over_interaction_limit && (
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            color="warning.main"
                                                            sx={{ display: "block" }}
                                                        >
                                                            {INTERACTION_LIMIT_MESSAGES[point.tipo]}
                                                        </Typography>
                                                    )}
                                                    {point.created_at && (
                                                        <Typography
                                                            component="p"
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {(() => {
                                                                const date = new Date(point.created_at);
                                                                // Subtract 3 hours for GMT-3
                                                                // we are doing this because Posts are being saved in UTC
                                                                // but periods and rankings are in GMT-3
                                                                date.setHours(date.getHours() - 3);
                                                                return date.toLocaleString('pt-BR', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                    timeZone: 'UTC'
                                                                });
                                                            })()}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: point.over_interaction_limit ? "text.disabled" : "#00A3FF",
                                                ml: 2,
                                                alignSelf: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            +{point.points}
                                        </Typography>
                                    </ListItem>
                                    {index < pointDetails.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    ) : (
                        <Typography align="center" sx={{ my: 4, color: "text.secondary" }}>
                            Nenhum ponto encontrado para este período
                        </Typography>
                    )}
                </Box>

                <Button
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={handleGoBack}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1.5,
                        px: 4,
                    }}
                >
                    Voltar para o ranking
                </Button>
            </Box>
        </S.Container>
    );
};
